import { Button } from "@groot/ui/button";
import { PageHeader } from "@groot/shell/components/layout";
import { Page } from "@/app/shared/components/page";
import { Input } from "@groot/ui/input";
import { LoadingSpinner } from "@groot/ui/loading-spinner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@groot/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@groot/ui/table";
import { useConfirm } from "@groot/ui/primitives";
import { toast } from "@/lib/toast";
import { formatDisplayDateTime } from "@/lib/utils";
import { apiClient } from "@/services/api";
import type { MobileNotification } from "@/types";
import { XCircle as ClearIcon, Search, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

export function MobileNotifications() {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [notifications, setNotifications] = useState<MobileNotification[]>([]);
  const [appPackages, setAppPackages] = useState<Array<{ package: string; name: string | null }>>(
    [],
  );
  const [devices, setDevices] = useState<string[]>([]);
  const [selectedAppPackage, setSelectedAppPackage] = useState<string>("all");
  const [selectedDevice, setSelectedDevice] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);

  const pageSize = 50;

  useEffect(() => {
    loadAppPackages();
    loadDevices();
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: loadData is not stable
  useEffect(() => {
    if (page !== 0) {
      setPage(0);
    } else {
      loadData();
    }
  }, [selectedAppPackage, selectedDevice, searchQuery]);

  useEffect(() => {
    if (page >= 0) {
      loadData();
    }
  }, [page]);

  const loadData = async () => {
    try {
      setLoading(true);

      const filters: {
        appPackage?: string;
        device?: string;
        search?: string;
        limit: number;
        offset: number;
      } = {
        limit: pageSize,
        offset: page * pageSize,
      };

      if (selectedAppPackage !== "all") {
        filters.appPackage = selectedAppPackage;
      }

      if (selectedDevice !== "all") {
        filters.device = selectedDevice;
      }

      if (searchQuery.trim()) {
        filters.search = searchQuery.trim();
      }

      const { notifications: notificationsData, total: totalCount } =
        await apiClient.getMobileNotifications(filters);

      setNotifications(notificationsData);
      setTotal(totalCount);
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to load mobile notifications",
      });
    } finally {
      setLoading(false);
    }
  };

  const loadAppPackages = async () => {
    try {
      const packagesData = await apiClient.getMobileNotificationAppPackages();
      setAppPackages(packagesData);
    } catch (error) {
      console.error("Failed to load app packages:", error);
    }
  };

  const loadDevices = async () => {
    try {
      const devicesData = await apiClient.getMobileNotificationDevices();
      setDevices(devicesData);
    } catch (error) {
      console.error("Failed to load devices:", error);
    }
  };

  const handleDelete = async (id: number) => {
    if (
      !(await confirm({
        title: "Confirm Action",
        description: "Are you sure you want to delete this notification?",
      }))
    ) {
      return;
    }

    try {
      await apiClient.deleteMobileNotification(id);
      toast({
        title: "Success",
        description: "Notification deleted successfully",
      });
      loadData();
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to delete notification",
      });
    }
  };

  const handleClearFilters = () => {
    setSelectedAppPackage("all");
    setSelectedDevice("all");
    setSearchQuery("");
  };

  const hasActiveFilters =
    selectedAppPackage !== "all" || selectedDevice !== "all" || searchQuery.trim() !== "";

  return (
    <Page>
      {/* Header */}
      <PageHeader
        title="Mobile Notifications"
        description={`${total} ${total === 1 ? "notification" : "notifications"}`}
        className="mb-8"
      />

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-3 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground/70" />
          <Input
            placeholder="Search notifications..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 border-input"
          />
        </div>

        <Select value={selectedAppPackage} onValueChange={setSelectedAppPackage}>
          <SelectTrigger className="w-full md:w-[200px] border-input">
            <SelectValue placeholder="All Apps" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Apps</SelectItem>
            {appPackages.map((app) => (
              <SelectItem key={app.package} value={app.package}>
                {app.name || app.package}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={selectedDevice} onValueChange={setSelectedDevice}>
          <SelectTrigger className="w-full md:w-[180px] border-input">
            <SelectValue placeholder="All Devices" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Devices</SelectItem>
            {devices.map((device) => (
              <SelectItem key={device} value={device}>
                {device}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {hasActiveFilters && (
          <Button variant="outline" onClick={handleClearFilters} className="gap-2">
            <ClearIcon className="size-4" />
            Clear
          </Button>
        )}
      </div>

      {/* Notifications Table */}
      {loading ? (
        <LoadingSpinner size="lg" className="py-20" />
      ) : notifications.length === 0 ? (
        <div className="text-center py-20">
          <p className="text-muted-foreground">
            {hasActiveFilters ? "No notifications found" : "No notifications yet"}
          </p>
        </div>
      ) : (
        <>
          <div className="border border-border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted hover:bg-muted">
                  <TableHead className="font-medium text-foreground/80 w-[80px]">ID</TableHead>
                  <TableHead className="font-medium text-foreground/80">Date</TableHead>
                  <TableHead className="font-medium text-foreground/80">App</TableHead>
                  <TableHead className="font-medium text-foreground/80">Title</TableHead>
                  <TableHead className="font-medium text-foreground/80">Status</TableHead>
                  <TableHead className="w-[60px]" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {notifications.map((notification) => {
                  const appName =
                    appPackages.find((app) => app.package === notification.app_package)?.name ||
                    notification.app_package;
                  return (
                    <TableRow
                      key={notification.id}
                      className="cursor-pointer hover:bg-muted border-border"
                      onClick={() => navigate(`/mobile-notifications/${notification.id}`)}
                    >
                      <TableCell className="font-mono text-[0.9em] text-muted-foreground">
                        {notification.id}
                      </TableCell>
                      <TableCell className="text-sm text-foreground whitespace-nowrap">
                        {formatDisplayDateTime(notification.createdAt)}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground max-w-[150px] truncate">
                        {appName}
                      </TableCell>
                      <TableCell className="text-sm text-foreground max-w-md truncate">
                        {notification.title || notification.text || "(No title)"}
                      </TableCell>
                      {/* Notifications are not linked to transactions in the data model. */}
                      <TableCell className="text-sm text-muted-foreground/70">
                        No transactions
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="size-8 p-0 text-muted-foreground/70 hover:text-muted-foreground"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(notification.id);
                          }}
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between mt-4">
            <div className="text-sm text-muted-foreground">
              Showing {page * pageSize + 1} to {Math.min((page + 1) * pageSize, total)} of {total}
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(page - 1)}
                disabled={page === 0}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(page + 1)}
                disabled={(page + 1) * pageSize >= total}
              >
                Next
              </Button>
            </div>
          </div>
        </>
      )}
    </Page>
  );
}
